import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {get, ref, remove, set} from 'firebase/database';
import {createUserWithEmailAndPassword, getAuth} from 'firebase/auth';
import {database} from '../../config/firebase';
import {useAuth} from '../../hooks/useAuth';
import {encodeEmail} from "../../utils/utils";

const UserManagement = () => {
    const {userRole} = useAuth();
    const auth = getAuth();

    const [users, setUsers] = useState([]);
    const [churches, setChurches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // État du nouvel utilisateur
    const [showAddModal, setShowAddModal] = useState(false);
    const [newUser, setNewUser] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        role: 'church_admin',
        churchId: ''
    });
    const [addingUser, setAddingUser] = useState(false);
    const [addError, setAddError] = useState(null);

    // État de suppression d'utilisateur
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Charger les utilisateurs et les églises
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Vérifier que l'utilisateur est un admin
                if (userRole?.role !== 'admin') {
                    setError("Vous n'avez pas les permissions nécessaires pour accéder à cette page.");
                    setLoading(false);
                    return;
                }

                // Récupérer les rôles des utilisateurs
                const rolesRef = ref(database, 'roles');
                const rolesSnapshot = await get(rolesRef);

                // Récupérer les églises
                const churchesRef = ref(database, 'churches');
                const churchesSnapshot = await get(churchesRef);

                // Traiter les données des églises
                const churchesList = [];
                if (churchesSnapshot.exists()) {
                    churchesSnapshot.forEach((childSnapshot) => {
                        churchesList.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });

                    // Trier les églises par nom
                    churchesList.sort((a, b) => a.nom.localeCompare(b.nom));
                    setChurches(churchesList);

                    // S'il y a des églises, définir la première comme valeur par défaut pour un nouvel utilisateur
                    if (churchesList.length > 0) {
                        setNewUser(prev => ({...prev, churchId: churchesList[0].id}));
                    }
                }

                // Traiter les données des utilisateurs/rôles
                const usersList = [];
                if (rolesSnapshot.exists()) {
                    // Pour chaque rôle, récupérer les informations supplémentaires si disponibles
                    for (const [uid, roleData] of Object.entries(rolesSnapshot.val())) {
                        const userData = {uid, ...roleData};

                        // Si c'est un admin d'église, récupérer le nom de l'église
                        if (roleData.role === 'church_admin' && roleData.churchId) {
                            const church = churchesList.find(c => c.id === roleData.churchId);
                            if (church) {
                                userData.churchName = church.nom;
                            }
                        }

                        // Récupérer les informations de profil si disponibles
                        const userProfileRef = ref(database, `users/${uid}/profile`);
                        const userProfileSnapshot = await get(userProfileRef);

                        if (userProfileSnapshot.exists()) {
                            userData.profile = userProfileSnapshot.val();
                        }

                        usersList.push(userData);
                    }

                    // Trier par rôle (admin en premier, puis admin d'église)
                    usersList.sort((a, b) => {
                        if (a.role === 'admin' && b.role !== 'admin') return -1;
                        if (a.role !== 'admin' && b.role === 'admin') return 1;
                        return 0;
                    });

                    setUsers(usersList);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des données:", error);
                setError("Une erreur s'est produite lors du chargement des utilisateurs.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userRole]);

    // Gérer les changements dans le formulaire de nouvel utilisateur
    const handleNewUserChange = (e) => {
        const {name, value} = e.target;
        setNewUser(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Ajouter un nouvel utilisateur
    const handleAddUser = async (e) => {
        e.preventDefault();
        setAddingUser(true);
        setAddError(null);

        try {
            // Vérifier que les mots de passe correspondent
            if (newUser.password !== newUser.confirmPassword) {
                throw new Error("Les mots de passe ne correspondent pas.");
            }

            // Vérifier que les champs obligatoires sont remplis
            const requiredFields = ['email', 'password', 'role'];
            for (const field of requiredFields) {
                if (!newUser[field]) {
                    throw new Error(`Le champ ${field} est obligatoire.`);
                }
            }

            // Si c'est un admin d'église, vérifier qu'une église est sélectionnée
            if (newUser.role === 'church_admin' && !newUser.churchId) {
                throw new Error("Veuillez sélectionner une église pour l'administrateur d'église.");
            }

            // Créer l'utilisateur dans Firebase Authentication
            const userCredential = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password);
            const {uid} = userCredential.user;

            // Créer le rôle dans la base de données
            const roleData = {
                role: newUser.role
            };

            // Ajouter l'ID de l'église si c'est un admin d'église
            if (newUser.role === 'church_admin' && newUser.churchId) {
                roleData.churchId = newUser.churchId;
            }

            // Enregistrer le rôle
            await set(ref(database, `roles/${uid}`), roleData);

            // Créer une entrée dans la table de référence pour l'email
            const encodedEmail = encodeEmail(newUser.email);
            await set(ref(database, `userLookup/byEmail/${encodedEmail}`), uid);

            // Réinitialiser le formulaire et fermer le modal
            setNewUser({
                email: '',
                password: '',
                confirmPassword: '',
                role: 'church_admin',
                churchId: churches.length > 0 ? churches[0].id : ''
            });
            setShowAddModal(false);

            // Afficher un message de succès et recharger la page après un court délai
            setSuccess(`Utilisateur ${newUser.email} créé avec succès.`);
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error("Erreur lors de la création de l'utilisateur:", error);

            // Messages d'erreur spécifiques pour Firebase
            if (error.code === 'auth/email-already-in-use') {
                setAddError("Cette adresse email est déjà utilisée.");
            } else if (error.code === 'auth/invalid-email') {
                setAddError("Adresse email invalide.");
            } else if (error.code === 'auth/weak-password') {
                setAddError("Le mot de passe est trop faible. Il doit contenir au moins 6 caractères.");
            } else {
                setAddError(error.message);
            }
        } finally {
            setAddingUser(false);
        }
    };

    // Préparer la suppression d'un utilisateur
    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setShowDeleteModal(true);
    };

    // Supprimer un utilisateur
    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        setDeleting(true);

        try {
            const {uid} = selectedUser;

            // Supprimer le rôle
            await remove(ref(database, `roles/${uid}`));

            // Supprimer des tables de référence si email connu
            if (selectedUser.profile && selectedUser.profile.email) {
                const encodedEmail = encodeEmail(selectedUser.profile.email);
                await remove(ref(database, `userLookup/byEmail/${encodedEmail}`));
            }

            // Note: La suppression de l'utilisateur dans Firebase Authentication
            // nécessiterait des fonctions Cloud ou une authentification récente
            // Ce code supposera que l'utilisateur n'est plus dans Auth

            // Fermer le modal et mettre à jour la liste
            setShowDeleteModal(false);
            setSelectedUser(null);
            setSuccess(`Utilisateur supprimé avec succès.`);

            // Mettre à jour la liste sans recharger
            setUsers(users.filter(user => user.uid !== uid));
        } catch (error) {
            console.error("Erreur lors de la suppression de l'utilisateur:", error);
            setError(`Erreur lors de la suppression: ${error.message}`);
        } finally {
            setDeleting(false);
        }
    };

    // Obtenir le libellé du rôle
    const getRoleLabel = (role) => {
        switch (role) {
            case 'admin':
                return 'Administrateur général';
            case 'church_admin':
                return "Administrateur d'église";
            default:
                return 'Utilisateur';
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error && users.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                    <div className="mt-4">
                        <Link to="/dashboard" className="text-red-700 underline hover:text-red-600">
                            Retour au tableau de bord
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <div className="flex items-center">
                        <Link to="/dashboard"
                              className="inline-flex items-center mr-4 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                            <svg className="-ml-1 mr-1 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg"
                                 viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd"
                                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                      clipRule="evenodd"/>
                            </svg>
                            Tableau de bord
                        </Link>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mt-2">Gestion des utilisateurs</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        Gérez les administrateurs et leurs permissions.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                             fill="currentColor">
                            <path fillRule="evenodd"
                                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                  clipRule="evenodd"/>
                        </svg>
                        Ajouter un utilisateur
                    </button>
                </div>
            </div>

            {/* Messages de succès/erreur */}
            {error && (
                <div className="mt-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4" role="alert">
                    <p>{error}</p>
                </div>
            )}

            {success && (
                <div className="mt-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4" role="alert">
                    <p>{success}</p>
                </div>
            )}

            {/* Liste des utilisateurs */}
            <div className="mt-6">
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                {users.length} {users.length > 1 ? 'utilisateurs' : 'utilisateur'}
              </span>
                        </div>
                    </div>
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                        <tr>
                            <th scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Utilisateur
                            </th>
                            <th scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rôle
                            </th>
                            <th scope="col"
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Église
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.uid} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div
                                            className="flex-shrink-0 h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                            <svg className="h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg"
                                                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                                            </svg>
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {user.profile ? `${user.profile.prenom || ''} ${user.profile.nom || ''}` : 'Utilisateur sans profil'}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {user.profile?.email || 'Email non disponible'}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {getRoleLabel(user.role)}
                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {user.role === 'church_admin' ? (
                                        user.churchName || 'Église non spécifiée'
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={() => handleDeleteClick(user)}
                                        className="text-red-600 hover:text-red-900"
                                        disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                                    >
                                        Supprimer
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal d'ajout d'utilisateur */}
            {showAddModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
                     aria-modal="true">
                    <div
                        className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                             aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen"
                              aria-hidden="true">&#8203;</span>
                        <div
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <form onSubmit={handleAddUser}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div
                                            className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <svg className="h-6 w-6 text-indigo-600" xmlns="http://www.w3.org/2000/svg"
                                                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                                            </svg>
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900"
                                                id="modal-title">
                                                Ajouter un nouvel utilisateur
                                            </h3>
                                            <div className="mt-4 space-y-4">
                                                {addError && (
                                                    <div
                                                        className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 text-sm"
                                                        role="alert">
                                                        <p>{addError}</p>
                                                    </div>
                                                )}

                                                <div>
                                                    <label htmlFor="email"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Adresse email *
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="email"
                                                            id="email"
                                                            name="email"
                                                            value={newUser.email}
                                                            onChange={handleNewUserChange}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label htmlFor="password"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Mot de passe *
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="password"
                                                            id="password"
                                                            name="password"
                                                            value={newUser.password}
                                                            onChange={handleNewUserChange}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            required
                                                        />
                                                    </div>
                                                    <p className="mt-1 text-xs text-gray-500">Minimum 6 caractères</p>
                                                </div>

                                                <div>
                                                    <label htmlFor="confirmPassword"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Confirmer le mot de passe *
                                                    </label>
                                                    <div className="mt-1">
                                                        <input
                                                            type="password"
                                                            id="confirmPassword"
                                                            name="confirmPassword"
                                                            value={newUser.confirmPassword}
                                                            onChange={handleNewUserChange}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            required
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label htmlFor="role"
                                                           className="block text-sm font-medium text-gray-700">
                                                        Rôle *
                                                    </label>
                                                    <div className="mt-1">
                                                        <select
                                                            id="role"
                                                            name="role"
                                                            value={newUser.role}
                                                            onChange={handleNewUserChange}
                                                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                            required
                                                        >
                                                            <option value="admin">Administrateur général</option>
                                                            <option value="church_admin">Administrateur d'église
                                                            </option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {newUser.role === 'church_admin' && (
                                                    <div>
                                                        <label htmlFor="churchId"
                                                               className="block text-sm font-medium text-gray-700">
                                                            Église *
                                                        </label>
                                                        <div className="mt-1">
                                                            <select
                                                                id="churchId"
                                                                name="churchId"
                                                                value={newUser.churchId}
                                                                onChange={handleNewUserChange}
                                                                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                                required
                                                            >
                                                                <option value="">Sélectionnez une église</option>
                                                                {churches.map(church => (
                                                                    <option key={church.id}
                                                                            value={church.id}>{church.nom}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        disabled={addingUser}
                                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm ${addingUser ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {addingUser ? (
                                            <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                             fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                  strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Création...
                      </span>
                                        ) : 'Ajouter'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={() => {
                                            setShowAddModal(false);
                                            setAddError(null);
                                        }}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {showDeleteModal && selectedUser && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
                     aria-modal="true">
                    <div
                        className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                             aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen"
                              aria-hidden="true">&#8203;</span>
                        <div
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div
                                        className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg"
                                             fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            Supprimer l'utilisateur
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Êtes-vous sûr de vouloir supprimer
                                                l'utilisateur {selectedUser.profile?.email || selectedUser.uid} ? Cette
                                                action est irréversible et supprimera toutes les permissions associées.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleDeleteUser}
                                    disabled={deleting}
                                >
                                    {deleting ? (
                                        <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                           fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Suppression...
                    </span>
                                    ) : 'Supprimer'}
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedUser(null);
                                    }}
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
